import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { MessageCircle, RefreshCw, AlertCircle, CheckCircle, QrCode, Save, Eye, EyeOff, Key } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export const SpokiAdminConfig = ({ units = [] }) => {
  const [health, setHealth] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesError, setTemplatesError] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [unitConfig, setUnitConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pairing, setPairing] = useState(false);
  const [pairResponse, setPairResponse] = useState(null);
  const [allConfigs, setAllConfigs] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [assistantsError, setAssistantsError] = useState("");
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  // NEW (feb 2026): gestione API key + webhook secret per-Unit
  const [apiKeyInput, setApiKeyInput] = useState(""); // valore inserito in input (vuoto = lascia invariato)
  const [webhookSecretInput, setWebhookSecretInput] = useState("");
  const [revealApiKey, setRevealApiKey] = useState(false);
  const [revealWebhookSecret, setRevealWebhookSecret] = useState(false);

  const runDiagnostics = async () => {
    setDiagLoading(true);
    setDiagnostics(null);
    try {
      // Se è selezionata una Unit, diagnostica mirata; altrimenti globale
      const url = selectedUnitId
        ? `${API}/spoki/diagnostics?unit_id=${selectedUnitId}`
        : `${API}/spoki/diagnostics`;
      const r = await axios.get(url, { headers: authHeaders() });
      setDiagnostics(r.data);
    } catch (e) {
      setDiagnostics({ report: `Errore diagnostica: ${e?.response?.data?.detail || e.message}` });
    } finally { setDiagLoading(false); }
  };

  const copyDiagnostics = () => {
    if (diagnostics?.report) {
      navigator.clipboard.writeText(diagnostics.report);
      alert("Report copiato negli appunti: incollalo nella mail/ticket per il supporto Spoki.");
    }
  };

  const fetchAssistants = async () => {
    try {
      const r = await axios.get(`${API}/spoki/openai-assistants`, { headers: authHeaders() });
      setAssistants(r.data?.assistants || []);
      if (r.data?.error || r.data?.warning) setAssistantsError(r.data.error || r.data.warning); else setAssistantsError("");
    } catch (e) {
      setAssistantsError(e?.response?.data?.detail || e.message);
    }
  };

  const fetchHealth = async () => {
    try {
      const r = await axios.get(`${API}/spoki/health`, { headers: authHeaders() });
      setHealth(r.data);
    } catch (e) {
      setHealth({ status: "error", error: e.message });
    }
  };

  const fetchTemplates = async (unitId) => {
    try {
      const u = unitId || selectedUnitId;
      const url = u ? `${API}/spoki/templates?unit_id=${u}` : `${API}/spoki/templates`;
      const r = await axios.get(url, { headers: authHeaders() });
      setTemplates(r.data?.templates || []);
      if (r.data?.error || r.data?.warning) setTemplatesError(r.data.error || r.data.warning); else setTemplatesError("");
    } catch (e) {
      setTemplatesError(e?.response?.data?.detail || e.message);
    }
  };

  const fetchAllConfigs = async () => {
    try {
      const r = await axios.get(`${API}/spoki/unit-configs`, { headers: authHeaders() });
      setAllConfigs(r.data || []);
    } catch (e) { /* ignore */ }
  };

  const fetchUnitConfig = async (unitId) => {
    if (!unitId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/spoki/unit-configs/${unitId}`, { headers: authHeaders() });
      setUnitConfig(r.data);
      // Reset input dei secrets ad ogni cambio Unit
      setApiKeyInput("");
      setWebhookSecretInput("");
      setRevealApiKey(false);
      setRevealWebhookSecret(false);
    } catch (e) {
      setUnitConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const revealApiKeyHandler = async () => {
    if (!selectedUnitId) return;
    try {
      const r = await axios.get(`${API}/spoki/unit-configs/${selectedUnitId}/secrets`, { headers: authHeaders() });
      setApiKeyInput(r.data?.api_key || "");
      setRevealApiKey(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Impossibile rivelare la chiave");
    }
  };

  const revealWebhookSecretHandler = async () => {
    if (!selectedUnitId) return;
    try {
      const r = await axios.get(`${API}/spoki/unit-configs/${selectedUnitId}/secrets`, { headers: authHeaders() });
      setWebhookSecretInput(r.data?.webhook_secret || "");
      setRevealWebhookSecret(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Impossibile rivelare il secret");
    }
  };

  useEffect(() => {
    fetchHealth();
    fetchTemplates();
    fetchAllConfigs();
    fetchAssistants();
  }, []);

  useEffect(() => {
    if (selectedUnitId) {
      fetchUnitConfig(selectedUnitId);
      fetchTemplates(selectedUnitId);
    }
  }, [selectedUnitId]);

  const handleSave = async () => {
    if (!selectedUnitId || !unitConfig) return;
    setSaving(true);
    try {
      const payload = {
        whatsapp_number: unitConfig.whatsapp_number || null,
        welcome_template_name: unitConfig.welcome_template_name || null,
        welcome_template_language: unitConfig.welcome_template_language || "it",
        welcome_template_variables: unitConfig.welcome_template_variables || {},
        chatbot_system_prompt: unitConfig.chatbot_system_prompt || null,
        chatbot_enabled: !!unitConfig.chatbot_enabled,
        openai_assistant_id: unitConfig.openai_assistant_id || "",
      };
      // NEW (feb 2026): includi api_key / webhook_secret SOLO se l'utente li ha modificati
      // (revealApiKey=true significa che l'input è stato popolato — sia via "Mostra" sia digitato)
      if (revealApiKey || (apiKeyInput && apiKeyInput.length > 0)) {
        payload.api_key = apiKeyInput;
      }
      if (revealWebhookSecret || (webhookSecretInput && webhookSecretInput.length > 0)) {
        payload.webhook_secret = webhookSecretInput;
      }
      const r = await axios.patch(`${API}/spoki/unit-configs/${selectedUnitId}`, payload, { headers: authHeaders() });
      setUnitConfig(r.data);
      setApiKeyInput("");
      setWebhookSecretInput("");
      setRevealApiKey(false);
      setRevealWebhookSecret(false);
      fetchAllConfigs();
      // Refetch dei template (potrebbero ora essere accessibili se la key è stata appena salvata)
      fetchTemplates(selectedUnitId);
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handlePair = async () => {
    if (!selectedUnitId) return;
    setPairing(true);
    setPairResponse(null);
    try {
      const r = await axios.post(`${API}/spoki/unit-configs/${selectedUnitId}/pair`, {}, { headers: authHeaders() });
      setPairResponse(r.data);
      fetchUnitConfig(selectedUnitId);
    } catch (e) {
      setPairResponse({ error: e?.response?.data?.detail || e.message });
    } finally {
      setPairing(false);
    }
  };

  const statusBadge = () => {
    if (!health) return <Badge variant="secondary">…</Badge>;
    // Health globale: aggrega quante Unit hanno la key configurata
    if (health.scope === "global") {
      if ((health.units_with_api_key || 0) === 0) return <Badge variant="secondary">Nessuna Unit configurata</Badge>;
      if (health.units_with_api_key === health.units_total) return <Badge className="bg-green-600">{health.units_with_api_key}/{health.units_total} Unit attive</Badge>;
      return <Badge className="bg-amber-500">{health.units_with_api_key}/{health.units_total} Unit configurate</Badge>;
    }
    if (health.status === "ok") return <Badge className="bg-green-600">Connesso</Badge>;
    if (health.status === "no_api_key") return <Badge variant="secondary">Chiave non configurata</Badge>;
    return <Badge variant="destructive">Errore: {String(health.error || "").slice(0, 60)}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="spoki-admin-config">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MessageCircle className="w-6 h-6 text-green-600" /> Configurazione WhatsApp Spoki
        </h1>
        <div className="flex items-center gap-3">
          {statusBadge()}
          <Button variant="outline" size="sm" onClick={() => { fetchHealth(); fetchTemplates(); }} data-testid="spoki-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
          </Button>
        </div>
      </div>

      {health?.status === "error" && (
        <Card className="border-orange-300 bg-orange-50">
          <CardContent className="p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-orange-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-orange-900 flex-1">
              <strong>Connessione Spoki non attiva.</strong> Verifica nel pannello Spoki che l&apos;API key sia stata generata e attivata.
              <div className="mt-1 font-mono text-xs">{health.error}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Diagnostica connessione per supporto Spoki */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Diagnostica connessione Spoki</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={runDiagnostics} disabled={diagLoading} data-testid="spoki-diagnostics-btn">
              {diagLoading ? "Test in corso..." : "Esegui diagnostica"}
            </Button>
            {diagnostics?.report && (
              <Button size="sm" onClick={copyDiagnostics} data-testid="spoki-diagnostics-copy">
                Copia report per Spoki
              </Button>
            )}
          </div>
        </CardHeader>
        {diagnostics?.report && (
          <CardContent>
            <pre className="text-xs bg-slate-900 text-slate-100 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap" data-testid="spoki-diagnostics-report">
{diagnostics.report}
            </pre>
            <div className="text-xs text-slate-500 mt-2">
              Le chiamate a Spoki avvengono dal server del CRM (non dal browser): per questo gli errori non compaiono in Console/Network.
              Copia questo report e invialo al supporto Spoki.
            </div>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Seleziona Unit / Commessa</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedUnitId} onValueChange={setSelectedUnitId}>
            <SelectTrigger data-testid="spoki-unit-select">
              <SelectValue placeholder="Scegli una Unit..." />
            </SelectTrigger>
            <SelectContent>
              {units?.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.nome || u.label || u.id} {allConfigs.find(c => c.unit_id === u.id) ? "✓" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {selectedUnitId && unitConfig && !loading && (
        <Card data-testid="spoki-unit-config-card">
          <CardHeader>
            <CardTitle>Configurazione Unit</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* NEW (feb 2026): Credenziali Spoki per-Unit */}
            <div className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-4 space-y-3" data-testid="spoki-unit-credentials">
              <div className="flex items-center gap-2">
                <Key className="w-4 h-4 text-emerald-700" />
                <span className="text-sm font-semibold text-emerald-900">Credenziali Spoki di questa Unit</span>
                {unitConfig.api_key_configured ? (
                  <Badge className="bg-green-600 ml-auto">Chiave attiva</Badge>
                ) : (
                  <Badge variant="secondary" className="ml-auto">Chiave mancante</Badge>
                )}
              </div>
              <div className="text-xs text-emerald-900/80">
                Ogni Unit ha la sua API key Spoki dedicata. Per ottenerla: pannello Spoki → Integrazioni → API.
              </div>

              <div>
                <Label className="text-xs">Spoki API Key</Label>
                <div className="flex gap-2">
                  <Input
                    type={revealApiKey ? "text" : "password"}
                    data-testid="spoki-api-key-input"
                    value={revealApiKey || apiKeyInput ? apiKeyInput : (unitConfig.api_key_masked || "")}
                    onChange={(e) => { setApiKeyInput(e.target.value); setRevealApiKey(true); }}
                    placeholder={unitConfig.api_key_configured ? "(chiave salvata — clicca Mostra per modificare)" : "Incolla la API key Spoki"}
                    className="font-mono"
                  />
                  {unitConfig.api_key_configured && !revealApiKey && (
                    <Button variant="outline" size="sm" onClick={revealApiKeyHandler} data-testid="spoki-api-key-reveal">
                      <Eye className="w-4 h-4 mr-1" /> Mostra
                    </Button>
                  )}
                  {(revealApiKey || apiKeyInput) && (
                    <Button variant="outline" size="sm" onClick={() => { setRevealApiKey(false); setApiKeyInput(""); }} data-testid="spoki-api-key-hide">
                      <EyeOff className="w-4 h-4 mr-1" /> Nascondi
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-xs">Webhook Secret (X-Spoki-Signature)</Label>
                <div className="flex gap-2">
                  <Input
                    type={revealWebhookSecret ? "text" : "password"}
                    data-testid="spoki-webhook-secret-input"
                    value={revealWebhookSecret || webhookSecretInput ? webhookSecretInput : (unitConfig.webhook_secret_masked || "")}
                    onChange={(e) => { setWebhookSecretInput(e.target.value); setRevealWebhookSecret(true); }}
                    placeholder={unitConfig.webhook_secret_configured ? "(secret salvato — clicca Mostra per modificare)" : "Incolla il webhook signing secret"}
                    className="font-mono"
                  />
                  {unitConfig.webhook_secret_configured && !revealWebhookSecret && (
                    <Button variant="outline" size="sm" onClick={revealWebhookSecretHandler} data-testid="spoki-webhook-secret-reveal">
                      <Eye className="w-4 h-4 mr-1" /> Mostra
                    </Button>
                  )}
                  {(revealWebhookSecret || webhookSecretInput) && (
                    <Button variant="outline" size="sm" onClick={() => { setRevealWebhookSecret(false); setWebhookSecretInput(""); }} data-testid="spoki-webhook-secret-hide">
                      <EyeOff className="w-4 h-4 mr-1" /> Nascondi
                    </Button>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Usato per verificare la firma dei webhook in arrivo (HMAC SHA-256). Lascia vuoto per disabilitare il check.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Numero WhatsApp (E.164, es. +393331234567)</Label>
                <Input
                  data-testid="spoki-whatsapp-number"
                  value={unitConfig.whatsapp_number || ""}
                  onChange={(e) => setUnitConfig({ ...unitConfig, whatsapp_number: e.target.value })}
                  placeholder="+393331234567"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Stato pairing: <Badge variant={unitConfig.pairing_status === "connected" ? "default" : "secondary"}>{unitConfig.pairing_status || "not_paired"}</Badge>
                </div>
              </div>
              <div>
                <Label>Template di Benvenuto</Label>
                <Select
                  value={unitConfig.welcome_template_name || ""}
                  onValueChange={(v) => setUnitConfig({ ...unitConfig, welcome_template_name: v })}
                >
                  <SelectTrigger data-testid="spoki-template-select">
                    <SelectValue placeholder={templates.length ? "Scegli template..." : "Nessun template disponibile"} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t, i) => (
                      <SelectItem key={i} value={t.name || t.id || `tpl-${i}`}>
                        {t.name || t.id || `Template ${i+1}`} {t.language ? `(${t.language})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {templatesError && <div className="text-xs text-red-600 mt-1">{templatesError}</div>}
              </div>
              <div>
                <Label>Lingua template</Label>
                <Input
                  value={unitConfig.welcome_template_language || "it"}
                  onChange={(e) => setUnitConfig({ ...unitConfig, welcome_template_language: e.target.value })}
                  placeholder="it"
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  data-testid="spoki-chatbot-switch"
                  checked={!!unitConfig.chatbot_enabled}
                  onCheckedChange={(v) => setUnitConfig({ ...unitConfig, chatbot_enabled: v })}
                />
                <Label>Chatbot OpenAI attivo</Label>
              </div>
            </div>

            <div>
              <Label>Assistant OpenAI (bot della Unit)</Label>
              <Select
                value={unitConfig.openai_assistant_id || ""}
                onValueChange={(v) => setUnitConfig({ ...unitConfig, openai_assistant_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="spoki-assistant-select">
                  <SelectValue placeholder={assistants.length ? "Scegli l'Assistant..." : "Nessun Assistant disponibile"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nessuno (usa chatbot interno con prompt) —</SelectItem>
                  {assistants.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name || a.id} ({a.model})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {assistantsError && <div className="text-xs text-red-600 mt-1">{assistantsError}</div>}
              <div className="text-xs text-gray-500 mt-1">
                Il bot risponde su WhatsApp SOLO dopo che il workflow lo ha attivato (nodo &quot;Attiva Chatbot AI&quot;).
                Se selezioni un Assistant, il system prompt qui sotto viene ignorato (usa le istruzioni dell&apos;Assistant su OpenAI).
              </div>
            </div>

            <div>
              <Label>System prompt chatbot (italiano)</Label>
              <Textarea
                data-testid="spoki-system-prompt"
                rows={8}
                value={unitConfig.chatbot_system_prompt || ""}
                onChange={(e) => setUnitConfig({ ...unitConfig, chatbot_system_prompt: e.target.value })}
                placeholder="Sei l'assistente di Nureal..."
              />
              <div className="text-xs text-gray-500 mt-1">
                Il bot deve rispondere SOLO con JSON nel formato richiesto. Modifica con cautela il prompt di default.
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={handleSave} disabled={saving} data-testid="spoki-save-config">
                <Save className="w-4 h-4 mr-1" /> {saving ? "Salvataggio..." : "Salva configurazione"}
              </Button>
              <Button variant="outline" onClick={handlePair} disabled={pairing || !health?.api_key_configured} data-testid="spoki-pair-number">
                <QrCode className="w-4 h-4 mr-1" /> {pairing ? "Generazione QR..." : "Associa numero (QR)"}
              </Button>
            </div>

            {pairResponse && (
              <Card className={pairResponse.error ? "border-red-300 bg-red-50" : "border-blue-300 bg-blue-50"}>
                <CardContent className="p-3 text-sm">
                  {pairResponse.error ? (
                    <div className="text-red-800">{pairResponse.error}</div>
                  ) : (
                    <>
                      <div className="font-semibold mb-1 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Sessione pairing aperta</div>
                      <pre className="text-xs overflow-x-auto">{JSON.stringify(pairResponse.spoki_response || pairResponse, null, 2)}</pre>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SpokiAdminConfig;
